// ═══════════════════════════════════════════════════════════════
// ITRIIA BENCH v0.1 — Profils modèles
// ═══════════════════════════════════════════════════════════════
//
// Source de vérité pour le SummarizerModelPicker et les tooltips.
// Données issues du run benchmark 2026-04-12 sur
// ARCHITECTURE-ITRIIA-ECOSYSTEME.md (102 KB, ArticleHandler).
//
// Limitations assumées :
//   - 1 document testé, 1 évaluateur, 6 modèles
//   - Résultats indicatifs, base amenée à être enrichie
//   - Les scores reflètent la qualité de SYNTHÈSE FACTUELLE,
//     pas la capacité générale du modèle
//
// Structure : export un objet indexé par clé symbolique (la même
// clé que MODEL_SYMBOLIC_MAP dans ExpertiseSummarizer.js côté
// backend). Chaque profil porte les données bench + metadata UI.
// ═══════════════════════════════════════════════════════════════

/**
 * Metadata du run benchmark
 */
export const BENCH_RUN = {
    slug: '2026-04-12-c1bis-article',
    date: '2026-04-12',
    document: 'ARCHITECTURE-ITRIIA-ECOSYSTEME.md',
    document_size_kb: 102,
    template: 'base-thematique-specialisee',
    handler: 'ArticleHandler',
    sections_total: 84,
    sections_indexed: 77,
    evaluator: 'Soufari Benabdellah',
    limitations: '1 document · 1 évaluateur · 6 modèles — base amenée à être enrichie',
};

/**
 * Profils modèles — indexés par clé symbolique
 */
export const MODEL_PROFILES = {
    // ── Défaut template thématique ────────────────────────────
    kimi: {
        symbolic: 'kimi',
        id: 'moonshotai/kimi-k2',
        label: 'Kimi K2',
        provider: 'Moonshot',
        tier: 'premium',
        clan: 'direct',
        // Scores bench (run 2026-04-12)
        bench: {
            rank: 6,
            score: 18,
            max_score: 25,
            fidelity: 3,
            density: 5,
            structure: 3,
            style: 2,
            neutrality: 5,
            l1_success: 77,
            l1_total: 84,
            l2_chars: 3500,
            tree_size_kb: 109,
            cost_run_usd: 0.14,
            cost_per_point: 0.008,
        },
        strengths: [
            'Ultra-économique ($0.008/point)',
            'Densité max — ports, seuils, tailles exactes',
            'Traitement rapide (~2-5s/section)',
        ],
        cautions: [
            'Erreurs typographiques possibles (parsing hasardeux)',
            'Format listing brut, pas narratif',
            'Pas adapté si le résumé doit être lu par des non-techniciens',
        ],
        recommendation: 'Défaut template — volume élevé, budget serré',
        tier_label: 'Économique',
        tier_color: '#22c55e',
    },

    // ── Compromis qualité/prix ───────────────────────────────
    'deepseek-r1': {
        symbolic: 'deepseek-r1',
        id: 'deepseek/deepseek-r1',
        label: 'DeepSeek R1',
        provider: 'DeepSeek',
        tier: 'premium',
        clan: 'thinking',
        bench: {
            rank: 5,
            score: 19,
            max_score: 25,
            fidelity: 4,
            density: 4,
            structure: 4,
            style: 4,
            neutrality: 3,
            l1_success: 77,
            l1_total: 84,
            l2_chars: 3500,
            tree_size_kb: 118,
            cost_run_usd: 0.33,
            cost_per_point: 0.017,
        },
        strengths: [
            'Meilleur compromis qualité/prix ($0.017/point)',
            'Équilibré entre technique et lisibilité',
            'Bonne couverture des modules métier (gouvernance, overlay)',
        ],
        cautions: [
            'Thinking model — latence 8-15s par section',
            'Moins de détails infrastructure que Opus/GPT-5',
        ],
        recommendation: 'Compromis — 83% de la qualité Opus pour 7x moins cher',
        tier_label: 'Compromis',
        tier_color: '#3b82f6',
    },

    // ── Sweet spot Anthropic ─────────────────────────────────
    sonnet: {
        symbolic: 'sonnet',
        id: 'anthropic/claude-sonnet-4.6',
        label: 'Cl. Sonnet 4.6',
        provider: 'Anthropic',
        tier: 'balanced',
        clan: 'direct',
        bench: {
            rank: 3,
            score: 20,
            max_score: 25,
            fidelity: 4,
            density: 4,
            structure: 4,
            style: 3,
            neutrality: 5,
            l1_success: 77,
            l1_total: 84,
            l2_chars: 3500,
            tree_size_kb: 123,
            cost_run_usd: 1.02,
            cost_per_point: 0.051,
        },
        strengths: [
            'Sweet spot Anthropic — 87% Opus à 44% du prix',
            'Détails précis uniques (35+54 méthodes Pierre, completeness_score)',
            'Factualité irréprochable, neutralité parfaite',
            'Level1 le plus dense (avg 1018 chars)',
        ],
        cautions: [
            'Formatage parfois excessif (bold, séparateurs)',
            'Pas de raisonnement étendu (modèle direct)',
        ],
        recommendation: 'Équilibré — le meilleur milieu de gamme, recommandé si budget permet',
        tier_label: 'Équilibré',
        tier_color: '#8b5cf6',
    },

    // ── Couverture exhaustive ─────────────────────────────────
    'gpt5': {
        symbolic: 'gpt5',
        id: 'openai/gpt-5',
        label: 'GPT-5',
        provider: 'OpenAI',
        tier: 'oracle',
        clan: 'thinking',
        bench: {
            rank: 2,
            score: 22,
            max_score: 25,
            fidelity: 5,
            density: 5,
            structure: 3,
            style: 4,
            neutrality: 5,
            l1_success: 70,
            l1_total: 84,
            l2_chars: 4998,
            tree_size_kb: 159,
            cost_run_usd: 1.61,
            cost_per_point: 0.073,
        },
        strengths: [
            'Couverture la plus exhaustive de tous les modèles',
            'Mentionne Phase A cleanup, conventions SQL, headers suspension',
            'Résumé level2 le plus long (4998 chars) — rien n\'est oublié',
        ],
        cautions: [
            'Pas de titres H2 — mur de texte dense',
            'Thinking model — 7 sections en empty_response (70/84)',
            'Coûteux ($0.073/point)',
        ],
        recommendation: 'Exhaustif — quand on ne veut rien rater, audit et analyse complète',
        tier_label: 'Exhaustif',
        tier_color: '#f59e0b',
    },

    // ── Premium absolu ───────────────────────────────────────
    opus: {
        symbolic: 'opus',
        id: 'anthropic/claude-opus-4.6',
        label: 'Cl. Opus 4.6',
        provider: 'Anthropic',
        tier: 'oracle',
        clan: 'thinking-light',
        bench: {
            rank: 1,
            score: 23,
            max_score: 25,
            fidelity: 5,
            density: 5,
            structure: 5,
            style: 4,
            neutrality: 4,
            l1_success: 77,
            l1_total: 84,
            l2_chars: 3500,
            tree_size_kb: 113,
            cost_run_usd: 2.30,
            cost_per_point: 0.100,
        },
        strengths: [
            'Structure H2 exemplaire, progression logique top-down',
            'Fidélité et densité maximales (5/5 sur les deux)',
            'Le résumé sert de "carte du document" — lisible comme un sommaire',
        ],
        cautions: [
            'Le plus cher du benchmark ($0.10/point)',
            'Réservé aux corpus de référence durables',
        ],
        recommendation: 'Premium — corpus méthodologiques, docs de référence archivés',
        tier_label: 'Premium',
        tier_color: '#ef4444',
    },

    // ── Narratif / pédagogique (déconseillé rapport qualité/prix) ─
    'gemini-3-pro': {
        symbolic: 'gemini-3-pro',
        id: 'google/gemini-3.1-pro-preview',
        label: 'Gemini 3.1 Pro',
        provider: 'Google',
        tier: 'oracle',
        clan: 'thinking-heavy',
        bench: {
            rank: 4,
            score: 20,
            max_score: 25,
            fidelity: 4,
            density: 3,
            structure: 4,
            style: 5,
            neutrality: 4,
            l1_success: 77,
            l1_total: 84,
            l2_chars: 3399,
            tree_size_kb: 83,
            cost_run_usd: 3.27,
            cost_per_point: 0.164,
        },
        strengths: [
            'Le plus lisible pour les non-techniciens',
            'Approche narrative pédagogique, style rédactionnel élégant',
        ],
        cautions: [
            'Le plus cher du benchmark ($0.164/point) — 2x Opus pour le même score',
            'Verbeux — rhétorique sans information ajoutée',
            'Thinking model lourd — reasoning budget consomme beaucoup',
            'Level1 le plus court (avg 496 chars)',
        ],
        recommendation: 'Déconseillé rapport qualité/prix — sauf si style narratif explicitement voulu',
        tier_label: 'Déconseillé',
        tier_color: '#6b7280',
    },
};

/**
 * Modèles écartés du benchmark avec raisons (pour la page Aide)
 */
export const EXCLUDED_MODELS = [
    {
        id: 'openai/gpt-5-pro',
        label: 'GPT-5 Pro',
        reason: 'Latence batch pathologique (~95s/call × 77 sections = ~2h). Reasoning budget hors contrôle (0/84 success au tour 2). $0.30/call. Réservé aux analyses one-shot.',
    },
    {
        id: 'z-ai/glm-5.1',
        label: 'GLM 5.1',
        reason: 'Incompatible pipeline — reasoning_tokens consomment 100% du max_tokens, retour content=null systématique.',
    },
    {
        id: 'z-ai/glm-4.7',
        label: 'GLM 4.7',
        reason: 'Même problème que GLM 5.1 — content=4 chars, reasoning_tokens=2882.',
    },
    {
        id: 'z-ai/glm-4.6',
        label: 'GLM 4.6',
        reason: 'Fonctionne mais latence ~68-130s/call via OpenRouter. À retester via API directe Zhipu.',
    },
];

/**
 * Liste ordonnée pour le picker (du plus économique au plus premium)
 */
export const PICKER_ORDER = ['kimi', 'deepseek-r1', 'sonnet', 'gpt5', 'opus', 'gemini-3-pro'];

/**
 * Lookup rapide par clé symbolique
 */
export const getProfile = (symbolic) => MODEL_PROFILES[symbolic] || null;

/**
 * Lookup par ID OpenRouter complet
 */
export const getProfileByOpenRouterId = (id) =>
    Object.values(MODEL_PROFILES).find(p => p.id === id) || null;
